import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationPublishedSideSheet } from '@gitbook/app-navigation';
import { Icon as FAICon } from '@gitbook/icons';
import { Icon } from '@gitbook/spine-icons';
import { Button, Description, Dialog, DialogRoot, Heading, TextInput } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useNavigation, useSideSheetNavigation } from '../../../../navigation';

export function useTogglePublishedModal() {
    const { navigate } = useNavigation();

    return useEventCallback((open: boolean) => {
        navigate(
            {
                screen: {
                    sideSheet: open ? { type: 'published' } : undefined,
                },
            },
            { replace: true }
        );
    });
}

function usePublishedModalState(props: { site: api.Site }) {
    const state = useSideSheetNavigation<NavigationPublishedSideSheet>('published');
    const setOpen = useTogglePublishedModal();
    return [props.site.published && state.opened, setOpen] as const;
}

export function NavigablePublishSuccessDialog(props: {
    organization: api.Organization;
    site: api.Site;
}) {
    const [isOpen, setIsOpen] = usePublishedModalState(props);
    return (
        <DialogRoot isOpen={isOpen} onOpenChange={setIsOpen}>
            <Dialog>
                <PublishedDialogContent {...props} />
            </Dialog>
        </DialogRoot>
    );
}

function PublishedDialogContent(props: { organization: api.Organization; site: api.Site }) {
    const { site, organization } = props;
    const logShareEvent = (platform: 'twitter' | 'linkedin' | 'reddit') => {
        analytics.logEvent(
            analytics.createSiteEvent(
                { id: site.id, organization: organization.id },
                { type: 'site_share', triggerLocation: 'published-dialog', platform }
            )
        );
    };
    return (
        <div className="flex flex-col gap-6 p-8">
            {site.urls.published ? (
                <>
                    <div className="flex flex-col">
                        <Icon.LogoSquare size={40} className="mb-4" />
                        <Heading>Your site is now live!</Heading>
                        <Description>Let’s share your new docs with the world.</Description>
                    </div>
                    <TextInput
                        leadingIcon={Icon.Link}
                        value={site.urls.published || ''}
                        readOnly
                        copyable
                        autoFocus
                        onCopy={() => {
                            analytics.logEvent(
                                analytics.createSiteEvent(
                                    { id: site.id, organization: organization.id },
                                    {
                                        type: 'site_copy_link',
                                        triggerLocation: 'published-dialog',
                                    }
                                )
                            );
                        }}
                    />
                    <div className="flex flex-col gap-2">
                        <div className="font-medium text-muted">Share online:</div>
                        <div className="flex flex-wrap gap-4">
                            <TweetButton
                                url={site.urls.published}
                                onPress={() => logShareEvent('twitter')}
                            />
                            <LinkedInButton
                                url={site.urls.published}
                                onPress={() => logShareEvent('linkedin')}
                            />
                            <RedditButton
                                url={site.urls.published}
                                onPress={() => logShareEvent('reddit')}
                            />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="flex flex-col">
                        <Icon.LogoSquare size={40} className="mb-4" />
                        <Heading>Your site is now live!</Heading>
                        <Description>
                            You can now create share links to share your site with specific people.
                        </Description>
                    </div>
                    <Button
                        className="self-start"
                        kind="primary"
                        label="Manage share links"
                        leadingIcon={Icon.PublishStatusShareLink}
                        href={{
                            screen: {
                                type: 'site',
                                organization: organization.id,
                                site: site.id,
                                tab: { type: 'settings', section: 'audience' },
                            },
                        }}
                        onPress={() => {
                            analytics.logEvent(
                                analytics.createSiteEvent(
                                    { id: site.id, organization: organization.id },
                                    {
                                        type: 'site_manage_share_links',
                                        triggerLocation: 'published-dialog',
                                    }
                                )
                            );
                        }}
                    />
                </>
            )}
        </div>
    );
}

function TweetButton(props: { url: string; onPress?: () => void }) {
    const url = new URL('https://x.com/intent/tweet');
    url.searchParams.set('text', `Check out our new docs on @GitBookIO!\n${props.url}`);
    return (
        <Button
            kind="secondary"
            size="xlarge"
            iconOnly
            leadingIcon={<FAICon icon="x-twitter" />}
            label="Share on X"
            href={url.toString()}
            target="_blank"
            onPress={props.onPress}
        />
    );
}

function LinkedInButton(props: { url: string; onPress?: () => void }) {
    const url = new URL('https://www.linkedin.com/sharing/share-offsite/');
    url.searchParams.set('url', props.url);
    return (
        <Button
            kind="secondary"
            size="xlarge"
            iconOnly
            leadingIcon={<FAICon icon="linkedin" />}
            label="Share on LinkedIn"
            href={url.toString()}
            target="_blank"
            onPress={props.onPress}
        />
    );
}

function RedditButton(props: { url: string; onPress?: () => void }) {
    const url = new URL('https://reddit.com/submit');
    url.searchParams.set('url', props.url);
    url.searchParams.set('title', `Check out our new docs on GitBook!`);

    return (
        <Button
            kind="secondary"
            size="xlarge"
            iconOnly
            leadingIcon={<FAICon icon="reddit" />}
            label="Share on Reddit"
            href={url.toString()}
            target="_blank"
            onPress={props.onPress}
        />
    );
}
